"use client";

import React, { useEffect, useState } from "react";
import { OrderListItem, PAYMENT_METHOD, IS_DEBIT,ORDER_STATUS, PAYMENT_METHOD_LABELS } from "../services/order-service";
import OrderService from "../services/order-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import QRCode from "react-qr-code";

interface OrderPayModalProps {
  open: boolean;
  order: OrderListItem | null;
  onClose: () => void;
  onConfirm: (payload: { is_debit: IS_DEBIT;}) => void;
}

const OrderPayModal: React.FC<OrderPayModalProps> = ({
  open,
  order,
  onClose,
  onConfirm,
}) => {
  const [method, setMethod] = useState<PAYMENT_METHOD>(PAYMENT_METHOD.CASH);
  const [isDebit, setIsDebit] = useState(false);
  const { fetch: generateQR, loading: generating } = OrderService.generatePaymentQR();
  const { fetch: cancelQR, loading: cancelling } = OrderService.cancelPaymentQR();
  const { fetch: fetchQRDetail } = OrderService.detailPaymentQR();
  const [qrData, setQrData] = useState<any | null>(null);
  // Reset state when modal opens or order changes
  useEffect(() => {
    if (open) {
      setIsDebit(order?.is_debit === IS_DEBIT.DEBIT);
      // Đọc phương thức từ đơn hàng
      setMethod(Number(order?.payment_method) || PAYMENT_METHOD.CASH);
      setQrData(null);
    } else {
      // Dọn dẹp khi modal đóng
      setQrData(null);
    }
  }, [open, order]);
  
  if (!open || !order) return null;

  // Đơn chuyển khoản ĐÃ nhận tiền (ngân hàng báo về / kế toán gán tay): tiền về KHÔNG tự hoàn thành đơn nữa,
  // nên ở đây chỉ còn bước xác nhận hoàn thành — không chọn lại hình thức, không sinh QR.
  const isQrPaid =
    Number(order.payment_status) === 1 && Number(order.payment_method) === PAYMENT_METHOD.QR_CODE;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="relative bg-white rounded-xl w-[450px] max-w-[95vw] p-6 shadow-2xl">
        {/* Close (X) */}
        <button
          onClick={onClose}
          aria-label="Đóng"
          className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 text-xl leading-none"
        >
          ×
        </button>
        <h3 className="text-lg font-semibold mb-4 text-center">
          Xác nhận thanh toán
        </h3>
        <div className="space-y-3 text-sm">
          <p>
            <b>Mã đơn:</b> {order.code ?? `OD-${order.id}`}
          </p>
          <p>
            <b>Khách hàng:</b> {order.customer?.name ?? "—"}
          </p>
          <p>
            <b>Tổng tiền:</b>{" "}
            {Number(order.total_amount).toLocaleString("vi-VN")} ₫
          </p>

          {isQrPaid && (
            <div className="mt-3 rounded-lg bg-green-50 border border-green-200 p-3 text-green-700">
              Khách đã thanh toán chuyển khoản. Xác nhận để hoàn thành đơn.
            </div>
          )}

          {!qrData && !isQrPaid && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer mt-3">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  checked={isDebit}
                  onChange={(e) => setIsDebit(e.target.checked)}
                />
                <span className="font-medium">Ghi nợ (công nợ)</span>
              </label>

              {!isDebit && (
                <>
                  <label className="block text-sm font-medium">
                    Phương thức thanh toán:
                  </label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={method}
                    onChange={(e) =>
                      setMethod(Number(e.target.value) as PAYMENT_METHOD)
                    }
                    disabled={isDebit}
                  >
                    {Object.entries(PAYMENT_METHOD_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      )
                    )}
                  </select>
                </>
              )}
            </div>
          )}

          {qrData && (
            <div className="mt-3 text-center">
            <div className="font-medium text-slate-700 mb-2">
              Quét mã để thanh toán
            </div>
            <div className="text-xs text-slate-500 mb-2">
              Khi nhận được tiền, đơn chuyển sang đã thanh toán. Tải lại danh sách rồi mở lại đơn để hoàn thành.
            </div>
        
            {qrData.qr_code && (
              <div className="flex justify-center">
                <QRCode
                  value={String(qrData.qr_code)}
                  size={180}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="M"
                />
              </div>
            )}
        
            {qrData.checkout_url && (
              <a
                href={qrData.checkout_url}
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-2 text-green-600 hover:underline"
              >
                Mở trang thanh toán
              </a>
            )}
        
            <div className="mt-2 text-sm text-slate-500">
              Số tiền: {Number(qrData.amount ?? order.total_amount).toLocaleString("vi-VN")} ₫
            </div>
            {qrData.expired_at && (
              <div className="text-xs text-slate-400">
              Hết hạn:{" "}
              {qrData.expired_at
                ? new Date(qrData.expired_at).toLocaleString("vi-VN", {
                    hour12: false,
                    timeZone: "Asia/Ho_Chi_Minh",
                  })
                : "—"}
            </div>
            )}
          </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded">
            {qrData ? "Đóng" : "Hủy"}
          </button>
          {!qrData ? (
            <button
              onClick={async () => {
                if (isQrPaid) {
                  onConfirm({ is_debit: IS_DEBIT.NO_DEBIT });
                  return;
                }

                if (isDebit) {
                  onConfirm({ is_debit: IS_DEBIT.DEBIT });
                  return;
                }

                if (method === PAYMENT_METHOD.CASH) {
                  onConfirm({ is_debit: IS_DEBIT.NO_DEBIT });
                  return;
                }
                
                // QR flow
                if (!order?.id) return;
                try {
                  const res = await generateQR(Number(order.id), method);
                  if (res && isSuccess(res)) {
                    const data: any = res.data || {};
                    setQrData({
                      transaction_id: data.transaction_id,
                      order_id: data.order_id,
                      qr_code: data.qr_code || null,
                      checkout_url: data.checkout_url || null,
                      amount: data.amount ?? order.total_amount,
                      expired_at: data.expired_at,
                    });
                  } else {
                    showToast({
                      message: res?.message || "Không tạo được giao dịch thanh toán",
                      type: "error",
                    });
                  }
                } catch (e) {
                  showToast({
                    message: "Có lỗi khi tạo giao dịch thanh toán",
                    type: "error",
                  });
                }
              }}
              disabled={generating}
              className="px-5 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
            >
              {generating ? "Đang tạo QR..." : "Xác nhận"}
            </button>
          ) : (
            <>
              <button
                onClick={async () => {
                  if (!order?.id) return;

                  const confirmCancel = window.confirm("Bạn có chắc muốn huỷ giao dịch không?");
                  if (!confirmCancel) return;

                  const res = await cancelQR(Number(order.id));
                  if (res && isSuccess(res)) {
                    setQrData(null);
                    setMethod(PAYMENT_METHOD.CASH);
                    showToast({ message: "Đã huỷ giao dịch", type: "success" });
                  } else {
                    showToast({ message: res?.message || "Huỷ giao dịch thất bại", type: "error" });
                  }
                }}
                disabled={cancelling}
                className="px-5 py-2 border border-red-500 text-red-600 rounded hover:bg-red-50 disabled:opacity-60"
              >
                {cancelling ? "Đang huỷ..." : "Huỷ giao dịch"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderPayModal;
